import { useTranslation } from "react-i18next";
import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { BellIcon, X } from "lucide-react";
import useNotificationPermission from "@/hooks/useNotificationPermission";
import { Button } from "./ui/button";

const buildNotificationConfig = (t: (key: string) => string) => ({
  title: t("notifications.enabled_title"),
  body: t("notifications.enabled_body"),
  icon: "/favicon.ico",
});

const buildDialogContent = (t: (key: string) => string) => ({
  denied: {
    title: t("notifications.denied_title"),
    description: t("notifications.denied_desc"),
  },
  default: {
    title: t("notifications.default_title"),
    description: t("notifications.default_desc"),
  },
});

const DISMISSED_KEY = "notification-permission-dismissed";

export const NotificationPermissionDialog = () => {
  const { t } = useTranslation("common");
  const permission = useNotificationPermission();
  const [isOpen, setIsOpen] = useState(false);

  const isDenied = permission === "denied";
  const isDismissed = localStorage.getItem(DISMISSED_KEY) === "true";

  useEffect(() => {
    if (permission === "denied") {
      // Always show if denied - user needs to fix it
      setIsOpen(true);
    } else if (permission === "default" && !isDismissed) {
      // Only show if not dismissed before
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [permission, isDismissed]);

  const handleRequestPermission = useCallback(async () => {
    try {
      const result = await Notification.requestPermission();
      if (result === "granted") {
        setIsOpen(false);
        localStorage.removeItem(DISMISSED_KEY);
        new Notification(buildNotificationConfig(t).title, {
          body: buildNotificationConfig(t).body,
          icon: buildNotificationConfig(t).icon,
        });
      }
    } catch (error) {
      console.error("Error requesting notification permission:", error);
    }
  }, []);

  const handleDismiss = useCallback(() => {
    // Save dismissal so it doesn't show again
    localStorage.setItem(DISMISSED_KEY, "true");
    setIsOpen(false);
  }, []);

  const handleRefresh = useCallback(() => {
    window.location.reload();
  }, []);

  const dialogContent = buildDialogContent(t);
  const content = isDenied ? dialogContent.denied : dialogContent.default;

  return (
    <Dialog open={isOpen} modal>
      <DialogContent
        showCloseButton={false}
        className="sm:max-w-md text-danger"
        aria-describedby="notification-permission-description"
      >
        {/* Close Button */}
        <button
          onClick={handleDismiss}
          className="absolute end-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">{t("buttons.close")}</span>
        </button>

        <DialogHeader className="flex justify-center items-center">
          <span className="p-3 size-max rounded-full bg-danger-muted flex justify-center items-center mb-4">
            <BellIcon size={38} />
          </span>
          <DialogTitle className="text-center">{content.title}</DialogTitle>
          <DialogDescription
            id="notification-permission-description"
            className="text-center text-black/50"
          >
            {content.description}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="flex flex-col! items-center gap-2">
          {isDenied ? (
            <>
              <p className="text-black/50 font-semibold">
                {t("notifications.enabled_question")}
              </p>
              <Button className="w-full" onClick={handleRefresh}>
                {t("notifications.refresh_page")}
              </Button>
            </>
          ) : (
            <>
              <Button className="w-full" onClick={handleRequestPermission}>
                {t("notifications.default_title")}
              </Button>
              <Button
                variant="outline"
                className="w-full text-black"
                onClick={handleDismiss}
              >
                {t("notifications.maybe_later")}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
